import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import {
  CalendricalType,
  IPermitTypeField,
  PermitFieldType,
} from '~/client/graph'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { IWarningNote } from '~/client/src/shared/components/WarningNote/WarningNote'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { calendricalTypes } from '~/client/src/shared/utils/CalendricalTypeUtils'
import { getNonWorkTimeMessages } from '~/client/src/shared/utils/nonWorkTimeMessages'
import { truncateText } from '~/client/src/shared/utils/textUtils'
import { EMPTY_STRING, NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import { Checkbox } from '../../../shadcn-ui/checkbox'
import StruxhubInput from '../../../StruxhubInputs/StruxhubInput'
import StruxhubSelect from '../../../StruxhubInputs/StruxhubSelect'
import StruxhubTextValueSelector from '../../../StruxhubInputs/StruxhubSelector/StruxhubTextValueSelector'
import WeekdaysSelector from '../../../WeekdaysSelector/WeekdaysSelector'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import {
  doesNonWorkTimeBlock,
  getDateNotes,
  HoursOfOperationNoteList,
} from '../HoursOfOperationNote'
import PermitBaseFormField from './PermitBaseFormField'
import RecurringWorkflowOptions from './RecurringWorkflowOptions'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  projectDateStore?: ProjectDateStore
}

const MIN_FREQUENCY = 1
const MAX_INSPECTIONS_LABEL_LENGTH = 36

const inspectionFrequencyFieldCaption = 'Inspection frequency'
const useLogFrequencyCaption = 'Use log frequency'
const availableOnlyOnWorkingDays = '(Available only on working days)'
const nextInspections = (nextInspectionsString: string): string =>
  `Next: ${truncateText(nextInspectionsString, MAX_INSPECTIONS_LABEL_LENGTH)}`

@inject('projectDateStore')
@observer
export default class PermitFormStartEndDatesField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const { isViewMode, store, typeField, fieldsStore } = this.props
    const { caption, isMandatory } = typeField
    const { datesField } = store.workflowFieldsStore

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={[this.formattedDatesValue]}
        isViewMode={isViewMode}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={null}
        viewModeRenderer={this.renderViewMode}
      >
        <StruxhubTextValueSelector
          label={caption}
          isRequired={isMandatory}
          isChanged={datesField.isChanged}
          isValid={this.isDatesFieldShownValid}
          value={this.formattedDatesValue}
          onClick={this.openDatePicker}
          validationMessages={this.datesValidationMessages}
          note={
            !!this.notes.length && (
              <HoursOfOperationNoteList notes={this.notes} isSummarized />
            )
          }
        />
        {this.renderAllDayCheckbox()}
        {this.renderRecurringWorkflowOptions()}
        {this.inspectionFrequency}
      </PermitBaseFormField>
    )
  }

  private renderAllDayCheckbox(): JSX.Element {
    const { store } = this.props
    return (
      <div
        className="row y-center pl4 pointer tw:gap-2"
        onClick={store.toggleAllDay}
      >
        <Checkbox checked={store.editablePermit.isAllDay} />
        <span className="text large">{Localization.translator.allDay}</span>
      </div>
    )
  }

  private renderRecurringWorkflowOptions(): JSX.Element {
    const { store } = this.props
    if (!store.isRecurringWorkflowEnabled) return null

    return <RecurringWorkflowOptions sitePermitCreationFormStore={store} />
  }

  private renderViewMode = (value: string): JSX.Element => {
    return (
      <div className="col">
        <div className="text large word-break-all">{value}</div>
        <HoursOfOperationNoteList notes={this.notes} isSummarized isViewMode />
      </div>
    )
  }

  private get formattedDatesValue(): string {
    const { store, projectDateStore } = this.props
    return store.editablePermit.getDateAndTimeToDisplay(projectDateStore)
  }

  // The field turns red only for what no note below it explains.
  private get isDatesFieldShownValid(): boolean {
    const { store } = this.localProps
    const { startDate, endDate } = store.editablePermit
    if (store.workflowFieldsStore.datesField.isValid) return true
    return !!startDate && !!endDate && !this.datesValidationMessages?.length
  }

  private get notes(): IWarningNote[] {
    const { store, isViewMode } = this.localProps
    return getDateNotes(store, isViewMode)
  }

  private get inspectionFrequency(): JSX.Element {
    const {
      isViewMode,
      store,
      projectDateStore: {
        getSlashedMonthDayAndYear,
        getWeekdayNumber,
        hasWorkingDays,
      },
    } = this.props
    const {
      isInspectionPermit,
      inspectionFrequency,
      inspectionFrequencyType,
      shouldShowDaysToRepeat,
      selectedDaysToRepeat,
      inspectionMaxNumber,
      nextInspectionDates,
      changeDayToRepeat,
      editablePermit: { startDate },
      isEmissionFormType,
      isAdminOrFormsMaster,
    } = store

    if (!isInspectionPermit) {
      return null
    }

    const frequencyCaption = isEmissionFormType
      ? useLogFrequencyCaption
      : inspectionFrequencyFieldCaption
    const workingDaysLabel = hasWorkingDays
      ? availableOnlyOnWorkingDays
      : EMPTY_STRING

    const inspectionDatesString = nextInspectionDates
      ?.map(dt => getSlashedMonthDayAndYear(dt))
      .join(', ')

    if (isViewMode || !isAdminOrFormsMaster) {
      const element = (
        <div className="col">
          <div className="text large capitalize w-max-content">
            {this.inspectionLabel}
          </div>
          {inspectionDatesString && (
            <div className="text light large ellipsis pt10">
              {nextInspections(inspectionDatesString)}
            </div>
          )}
        </div>
      )

      return (
        <div className="row tw:gap-2.5 no-grow y-start py12">
          <PermitFieldIcon fieldName={PermitFieldType.AdditionalDate} />
          <div className="col pr6 overflow-hidden">
            <div className="text light large pb4 w-max-content">
              {frequencyCaption}
            </div>
            <div className="text light large pb4 w-max-content">
              {workingDaysLabel}
            </div>
            <div>{element || NO_VALUE}</div>
          </div>
        </div>
      )
    }

    const daysToRepeat = selectedDaysToRepeat?.length
      ? selectedDaysToRepeat
      : [+getWeekdayNumber(startDate)]

    return (
      <div className="row y-center no-grow">
        <div className="col">
          <div className="text light large my10">
            {frequencyCaption} {workingDaysLabel}
          </div>
          <div className="mb4 row x-between y-center no-outline-container">
            <span className="mr5 text light large capitalize">
              {Localization.translator.every_plural}
            </span>
            <div className="row">
              <StruxhubInput
                type="number"
                max={inspectionMaxNumber}
                min={MIN_FREQUENCY}
                value={inspectionFrequency?.toString()}
                onChange={this.changeInspectionFrequency}
                isMinimalisticMode={true}
              />
              <div className="mr8 no-grow" />
              <StruxhubSelect
                isMinimalisticMode={true}
                value={inspectionFrequencyType}
                onChange={this.changeInspectionFrequencyType}
              >
                {calendricalTypes.map(value => (
                  <option key={value} value={value}>
                    {this.getFrequencyTypeLabel(value)}
                  </option>
                ))}
              </StruxhubSelect>
            </div>
          </div>
          {shouldShowDaysToRepeat && (
            <div className="row x-between">
              <div className="text light large">
                {Localization.translator.repeatOn}
              </div>
              <WeekdaysSelector
                className="py10"
                selectedDayNumbers={daysToRepeat}
                onClick={changeDayToRepeat}
              />
            </div>
          )}
          {inspectionDatesString && (
            <div className="text light large ellipsis pt10">
              {nextInspections(inspectionDatesString)}
            </div>
          )}
        </div>
      </div>
    )
  }

  private getFrequencyTypeLabel = (frequencyType: CalendricalType): string => {
    const { inspectionFrequency } = this.props.store

    switch (frequencyType) {
      case CalendricalType.Day:
        return Localization.translator.xDays(inspectionFrequency)
      case CalendricalType.Week:
        return Localization.translator.xWeeks(inspectionFrequency)
      case CalendricalType.Month:
        return Localization.translator.xMonths(inspectionFrequency)
    }
  }

  private get inspectionLabel(): string {
    const { inspectionFrequency, inspectionFrequencyType } = this.props.store

    switch (inspectionFrequencyType) {
      case CalendricalType.Day:
        return Localization.translator.everyXDays(inspectionFrequency)
      case CalendricalType.Week:
        return Localization.translator.everyXWeeks(inspectionFrequency)
      case CalendricalType.Month:
        return Localization.translator.everyXMonths(inspectionFrequency)
    }
  }

  private get datesValidationMessages(): string[] {
    const { isViewMode, store, projectDateStore } = this.props
    const { startDate, endDate } = store.editablePermit
    const { getNonWorkTimeReasons } = projectDateStore

    if (isViewMode || !startDate || !endDate) {
      return null
    }

    // The note under the field explains a non-work time; this terse label is
    // kept for a blocking verdict the note can't explain. The store's verdicts
    // decide the wording, so a holiday in the middle of the interval does not
    // report itself as "non-work hours", and the set keeps the field from
    // saying the same thing twice (STX1-983).
    return doesNonWorkTimeBlock(store) && !store.projectHoursNote
      ? getNonWorkTimeMessages(getNonWorkTimeReasons(startDate, endDate))
      : []
  }

  private changeInspectionFrequency = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.props.store.applySpecificInspectionFrequency(
      event.target.valueAsNumber || 1,
    )
  }

  private changeInspectionFrequencyType = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this.props.store.applySpecificInspectionFrequencyType(
      event.target.value as CalendricalType,
    )
  }

  private openDatePicker = () => {
    const { store, typeField } = this.props
    store.setSelectedField(typeField)
  }
}
